import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Utensils, MapPin, Heart, Star, Users, CheckCircle, Clock,
  Coffee, Wine, Croissant, Truck, Store, ShoppingBasket,
  Search, Shield, Gift, TrendingUp, ArrowRight, IceCream2, Beer,
  Flame, Gamepad2, Music, Camera, TreePine, Eye, Megaphone, Bell,
  ShoppingBag, Smartphone
} from 'lucide-react';

const CATEGORIES = [
  { icon: Utensils, name: 'Restaurantes', desc: 'Comida nacional e internacional' },
  { icon: Coffee, name: 'Cafeterias', desc: 'El mejor cafe de Ecuador' },
  { icon: Wine, name: 'Restobares', desc: 'Cocteleria y gastronomia' },
  { icon: Croissant, name: 'Panaderias', desc: 'Pan artesanal y reposteria' },
  { icon: Truck, name: 'Carretas', desc: 'Comida sobre ruedas' },
  { icon: Flame, name: 'Agachaditos', desc: 'Sabor callejero tradicional' },
  { icon: Store, name: 'Tiendas Gourmet', desc: 'Productos selectos' },
  { icon: Beer, name: 'Bares', desc: 'Cervezas artesanales y mas' },
  { icon: IceCream2, name: 'Heladerias', desc: 'Helados y postres frios' },
  { icon: ShoppingBasket, name: 'Tiendas de Barrio', desc: 'Lo esencial cerca de ti' },
  { icon: Gamepad2, name: 'Salas de Juego', desc: 'Arcade, billar y mas' },
  { icon: Music, name: 'Clubes', desc: 'Eventos y entretenimiento' },
  { icon: Camera, name: 'Foto Estudio', desc: 'Captura tus mejores momentos' },
  { icon: TreePine, name: 'Parques', desc: 'Espacios recreativos' },
  { icon: ShoppingBag, name: 'Bazares', desc: 'Mochilas, ropa, accesorios y mas' },
  { icon: Smartphone, name: 'Tecnologia', desc: 'Electronica y gadgets' },
];

const FEATURES = [
  { icon: MapPin, title: 'Descubre cercanos', desc: 'Encuentra los mejores negocios y experiencias cerca de tu ubicacion en tiempo real.' },
  { icon: Heart, title: 'Guarda favoritos', desc: 'Marca tus lugares preferidos y accede a ellos rapidamente cuando quieras.' },
  { icon: Star, title: 'Gana puntos', desc: 'Acumula puntos por cada interaccion: visitas, compartidos, y participacion en sorteos.' },
  { icon: Users, title: 'Comparte con amigos', desc: 'Envia recomendaciones directamente a tus amigos dentro de la plataforma.' },
  { icon: Shield, title: 'Negocios verificados', desc: 'Todos los establecimientos pasan por un proceso de verificacion para garantizar calidad.' },
  { icon: Gift, title: 'Premios y sorteos', desc: 'Participa en la ruleta de premios y sorteos exclusivos para usuarios registrados.' },
];

const Welcome = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const justRegistered = searchParams.get('registered') === 'true';

  return (
    <div className="min-h-screen bg-white">
      {/* Registration Success Banner */}
      {justRegistered && (
        <div className="mx-4 mt-4 bg-teal-50 rounded-2xl p-5 border border-teal-200" data-testid="registration-banner">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-teal-600" />
            </div>
            <h3 className="text-lg font-bold text-teal-800">Registro Exitoso</h3>
          </div>
          <div className="flex items-start gap-2 mt-2">
            <Clock className="w-4 h-4 text-teal-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-teal-700">Tu cuenta esta en revision. Te notificaremos cuando sea aprobada.</p>
          </div>
        </div>
      )}

      {/* Hero */}
      <header className="bg-gradient-to-br from-teal-500 via-teal-600 to-emerald-700 px-6 pt-12 pb-16 text-center" data-testid="hero-section">
        <div className="w-24 h-24 bg-white rounded-2xl shadow-xl flex items-center justify-center mx-auto mb-5 p-2">
          <img src="/qplan-logo.png" alt="QPlan" className="w-full h-full object-contain" />
        </div>
        <h1 className="text-4xl font-bold text-white mb-3 tracking-tight">QPlan Ecuador</h1>
        <p className="text-teal-100 text-base max-w-xs mx-auto leading-relaxed">
          La plataforma mas completa del Ecuador para descubrir negocios, turismo y entretenimiento cerca de ti. Restaurantes, tiendas, clubes y mucho mas.
        </p>
        <div className="flex gap-3 mt-8 max-w-xs mx-auto">
          <button onClick={() => navigate('/login')} className="flex-1 py-3 bg-white text-teal-600 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all" data-testid="login-btn">
            Iniciar Sesion
          </button>
          <button onClick={() => navigate('/register')} className="flex-1 py-3 bg-teal-700/50 text-white rounded-xl font-semibold border border-white/30 hover:bg-teal-700/70 transition-all" data-testid="register-btn">
            Crear Cuenta
          </button>
        </div>
      </header>

      {/* Explore Public Button */}
      <div className="px-6 -mt-6 space-y-3">
        <button onClick={() => navigate('/explore')}
          className="w-full py-4 bg-white rounded-2xl shadow-lg border border-gray-100 flex items-center justify-center gap-3 hover:shadow-xl transition-all group"
          data-testid="explore-btn">
          <Search className="w-5 h-5 text-teal-500" />
          <span className="text-gray-700 font-semibold">Explorar negocios sin cuenta</span>
          <ArrowRight className="w-4 h-4 text-teal-500 group-hover:translate-x-1 transition-transform" />
        </button>
        <button onClick={() => navigate('/blog')}
          className="w-full py-4 bg-white rounded-2xl shadow-lg border border-gray-100 flex items-center justify-center gap-3 hover:shadow-xl transition-all group"
          data-testid="blog-btn">
          <Coffee className="w-5 h-5 text-amber-500" />
          <span className="text-gray-700 font-semibold">Blog QPlan</span>
          <ArrowRight className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Benefits - Users */}
      <section className="px-6 pt-8 pb-4" data-testid="user-benefits-section">
        <div className="bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-2xl p-5">
          <h2 className="text-base font-bold text-teal-800 mb-1">Para usuarios</h2>
          <p className="text-xs text-teal-600 mb-4">Descubre, explora y ahorra en tu ciudad</p>
          <div className="space-y-3">
            {[
              { icon: MapPin, title: 'Descubre negocios cerca', desc: 'Encuentra restaurantes, tiendas, clubes y mas cerca de tu ubicacion con el mapa interactivo.' },
              { icon: Heart, title: 'Guarda tus favoritos', desc: 'Marca los lugares que te gustan y accede a ellos rapidamente cuando quieras.' },
              { icon: Star, title: 'Gana puntos por interactuar', desc: 'Acumula puntos por visitas, compartidos y juegos del blog. Canjealos por premios.' },
              { icon: Gift, title: 'Participa en sorteos', desc: 'Gira la ruleta de premios y participa en sorteos exclusivos para usuarios registrados.' },
              { icon: Users, title: 'Comparte con amigos', desc: 'Recomienda lugares directamente a tus amigos dentro de la plataforma.' },
            ].map((b, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                  <b.icon className="w-4 h-4 text-teal-500" />
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-gray-800">{b.title}</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed text-justify">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits - Business Owners */}
      <section className="px-6 pb-8" data-testid="owner-benefits-section">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-5">
          <h2 className="text-base font-bold text-amber-800 mb-1">Para propietarios de negocios</h2>
          <p className="text-xs text-amber-600 mb-4">Registra tu negocio gratis y empieza a recibir visitas</p>
          <div className="space-y-3">
            {[
              { icon: Eye, title: 'Visibilidad inmediata', desc: 'Tu negocio aparece en el mapa y en Explorar donde miles de usuarios buscan opciones cerca de ellos.' },
              { icon: Shield, title: 'Registro gratuito', desc: 'Crea tu perfil, agrega sucursales, horarios, fotos y servicios sin costo. Visible desde el primer dia.' },
              { icon: TrendingUp, title: 'Estadisticas en tiempo real', desc: 'Conoce cuantas personas visitan tu perfil, te guardan como favorito y comparten tu negocio.' },
              { icon: Megaphone, title: 'Publica promociones', desc: 'Publica anuncios, eventos y ofertas que los usuarios ven directamente. Atrae clientes todos los dias.' },
              { icon: Bell, title: 'Notificaciones a tus fans', desc: 'Cuando publicas algo nuevo, los usuarios que te siguen y los cercanos reciben notificacion automatica.' },
              { icon: MapPin, title: 'Multiples ciudades', desc: 'Agrega todas tus sucursales desde un solo perfil. QPlan esta en Quito, Guayaquil, Cuenca y mas.' },
            ].map((b, i) => (
              <div key={i} className="flex gap-3">
                <div className="w-9 h-9 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                  <b.icon className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xs font-semibold text-gray-800">{b.title}</h3>
                  <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed text-justify">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => navigate('/register')} className="w-full mt-4 py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors text-sm" data-testid="owner-register-btn">
            Registra tu negocio gratis
          </button>
        </div>
      </section>

      {/* What is QPlan */}
      <section className="px-6 py-10" data-testid="about-section">
        <h2 className="text-lg font-bold text-gray-800 mb-3">Que es QPlan?</h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-4 text-justify">
          QPlan es una plataforma ecuatoriana creada para conectar a las personas con los mejores negocios y experiencias de su ciudad. Desde restaurantes y cafeterias hasta agachaditos, carretillas, tiendas de barrio, tiendas gourmet, salas de juego y clubes de entretenimiento, QPlan te permite descubrir, explorar y compartir experiencias unicas.
        </p>
        <p className="text-sm text-gray-600 leading-relaxed text-justify">
          Nuestra mision es impulsar la economia local y el turismo poniendo al alcance de todos los negocios, rincones gastronomicos y espacios de entretenimiento mas destacados del Ecuador. Con QPlan, cada salida es una aventura por descubrir.
        </p>
      </section>

      {/* Categories */}
      <section className="px-6 pb-10" data-testid="categories-section">
        <h2 className="text-lg font-bold text-gray-800 mb-4">Categorias de negocios</h2>
        <p className="text-sm text-gray-500 mb-5 text-justify">Explora una amplia variedad de negocios, establecimientos gastronomicos y espacios de entretenimiento en todo el Ecuador.</p>
        <div className="grid grid-cols-3 gap-3">
          {CATEGORIES.map((cat, i) => (
            <div key={i} className="bg-gray-50 rounded-xl p-3 text-center hover:bg-teal-50 transition-colors cursor-pointer" onClick={() => navigate('/explore')}>
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center mx-auto mb-2 shadow-sm">
                <cat.icon className="w-5 h-5 text-teal-500" strokeWidth={1.5} />
              </div>
              <p className="text-xs font-semibold text-gray-700">{cat.name}</p>
              <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">{cat.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="bg-gray-50 px-6 py-10" data-testid="features-section">
        <h2 className="text-lg font-bold text-gray-800 mb-2">Como funciona QPlan?</h2>
        <p className="text-sm text-gray-500 mb-6 text-justify">Descubre todas las funcionalidades que hacen de QPlan tu mejor aliado gastronomico.</p>
        <div className="space-y-4">
          {FEATURES.map((f, i) => (
            <div key={i} className="bg-white rounded-xl p-4 flex gap-4 shadow-sm">
              <div className="w-11 h-11 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <f.icon className="w-5 h-5 text-teal-500" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-gray-800">{f.title}</h3>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed text-justify">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* For Business Owners */}
      <section className="px-6 py-10" data-testid="owners-section">
        <h2 className="text-lg font-bold text-gray-800 mb-3">Para propietarios de negocios</h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-4 text-justify">
          QPlan ofrece a los propietarios de negocios gastronomicos una plataforma completa para dar visibilidad a sus establecimientos. Registra tu negocio, agrega sucursales, publica promociones y llega a miles de usuarios que buscan donde comer en tu ciudad.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { icon: TrendingUp, text: 'Estadisticas de visitas' },
            { icon: Store, text: 'Gestion de sucursales' },
            { icon: Gift, text: 'Publicaciones y promos' },
            { icon: Users, text: 'Alcanza nuevos clientes' },
          ].map((item, i) => (
            <div key={i} className="bg-emerald-50 rounded-xl p-3 flex items-center gap-2">
              <item.icon className="w-4 h-4 text-emerald-600 flex-shrink-0" strokeWidth={1.5} />
              <span className="text-xs font-medium text-emerald-700">{item.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Premium */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-50 px-6 py-10" data-testid="premium-section">
        <h2 className="text-lg font-bold text-gray-800 mb-3">QPlan Premium</h2>
        <p className="text-sm text-gray-600 leading-relaxed mb-4 text-justify">
          Mejora tu experiencia con QPlan Premium. Obtén acceso a contacto directo con negocios, promociones exclusivas, puntos dobles, y mucho más. Apoyar a los negocios locales nunca fue tan facil ni gratificante.
        </p>
        <button onClick={() => navigate('/register')} className="w-full py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 transition-colors">
          Registrate gratis para comenzar
        </button>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 px-6 py-8 text-center" data-testid="footer">
        <div className="flex items-center justify-center gap-2 mb-4">
          <img src="/qplan-logo.png" alt="QPlan" className="w-6 h-6 object-contain" />
          <span className="text-white font-bold">QPlan Ecuador</span>
        </div>
        <div className="flex flex-wrap justify-center gap-4 mb-4">
          <button onClick={() => navigate('/privacy-policy')} className="text-xs text-gray-400 hover:text-teal-400 transition-colors">Politica de Privacidad</button>
          <button onClick={() => navigate('/help')} className="text-xs text-gray-400 hover:text-teal-400 transition-colors">Ayuda y Contacto</button>
          <button onClick={() => navigate('/explore')} className="text-xs text-gray-400 hover:text-teal-400 transition-colors">Explorar Negocios</button>
          <button onClick={() => navigate('/blog')} className="text-xs text-gray-400 hover:text-teal-400 transition-colors">Blog</button>
        </div>
        <p className="text-xs text-gray-500">© 2026 QPlan Ecuador — Maralex-Ec. Todos los derechos reservados.</p>
        <p className="text-xs text-gray-600 mt-1">Quito, Ecuador</p>
      </footer>
    </div>
  );
};

export default Welcome;
